import { createContext, use, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import type { Me, OrgSummary, ProjectSummary, Role } from '@/api/schemas';

export interface Workspace {
  org: OrgSummary;
  project: ProjectSummary;
  role: Role;
  /** Resolved server side by the org, never chosen by the client. Plan D30. */
  reportingTimezone: string;
}

const WorkspaceContext = createContext<Workspace | null>(null);

/**
 * Resolve the `:projectId` in the URL against the memberships in `/v1/me`.
 *
 * A project id that is not in the response is not an error to explain, it is a
 * project this user cannot see, and plan 4.4 is explicit that a missing
 * resource and another tenant's resource are the same answer. `null` here
 * renders the same not found screen either way.
 */
export function findWorkspace(me: Me, projectId: string): Workspace | null {
  for (const org of me.orgs) {
    for (const project of org.projects) {
      if (project.project_id === projectId) {
        return {
          org,
          project,
          role: org.role,
          reportingTimezone: org.reporting_timezone,
        };
      }
    }
  }
  return null;
}

/** The first project of the first org, which the lazy bootstrap guarantees. */
export function defaultProject(me: Me): { org: OrgSummary; project: ProjectSummary } | null {
  for (const org of me.orgs) {
    const project = org.projects[0];
    if (project) return { org, project };
  }
  return null;
}

export function WorkspaceProvider({
  workspace,
  children,
}: {
  workspace: Workspace;
  children: ReactNode;
}): ReactNode {
  const value = useMemo(() => workspace, [workspace]);
  return <WorkspaceContext value={value}>{children}</WorkspaceContext>;
}

export function useWorkspace(): Workspace {
  const ctx = use(WorkspaceContext);
  if (ctx === null) {
    throw new Error('useWorkspace must be used inside a project route');
  }
  return ctx;
}

/**
 * The project id straight from the URL, before it has been resolved against
 * memberships. Used only by the route level loader that does the resolving.
 */
export function useProjectIdParam(): string | null {
  const params = useParams<{ projectId: string }>();
  return params.projectId ?? null;
}
